import { type Key, type ReactElement, type RefObject } from 'react';
import { type SubmenuTriggerAria } from 'react-aria';
import { type ItemProps, type RootMenuTriggerState, type SectionProps } from 'react-stately';

import { RouteManager } from '../../../RouteManager';
import { ToastManager } from '../../../ToastManager.jsx';
import { Menu, type MenuItemRenderState } from '../../common/Menu';

import { authStore, useAuth } from '../../../store/AuthStore';

import { useI18n } from '../../../hooks/useI18nFormatters';
import { useSelectedHomey } from '../../../store/SelectedHomeyStore.hooks';
import { useUserMe } from '../../../store/users/useUserMe';

import { suPx } from '../../../theme/functions/su';
import { glyph } from '../../../theme/glyph';
import styles from './MainNavProfileMenuContent.module.css';

import { type MainNavHomey, MainNavHomeyItem } from './MainNavHomeyItem';
import { MainNavProfileMenuItemContent } from './MainNavProfileMenuItemContent';

const ACCOUNT_URL = 'https://homey.app/account/';

const MENU_KEY = {
  presence: 'presence',
  appearance: 'appearance',
  homeys: 'homeys',
  account: 'account',
  signOut: 'signOut',
} as const;

interface MainNavProfileMenuContentProps {
  /** Hides My Presence, which needs an open Homey. */
  isMinimal: boolean;
  menuState: RootMenuTriggerState;
  onAppearancePress: () => void;
}

/**
 * Items of the profile menu, including the My Homeys submenu to switch between Homeys.
 */
export function MainNavProfileMenuContent(props: MainNavProfileMenuContentProps) {
  const { i18n } = useI18n();
  const { cloudUser } = useAuth();
  const selectedHomey = useSelectedHomey();
  const { userMe } = useUserMe();

  // Self-hosted servers have no Athom cloud account, so there are no other Homeys or account page.
  const isSelfHosted = import.meta.env.MODE === 'self-hosted';
  const homeys: MainNavHomey[] = cloudUser?.homeys ?? [];
  // Outside the Homey layout a Homey can be connected in the background without being open, so
  // none counts as the current one there.
  const currentHomeyId = props.isMinimal ? null : selectedHomey?.id;
  // userMe is missing when the Homey can't be reached, e.g. on the offline page.
  const isPresenceVisible = props.isMinimal === false && userMe != null;
  const isHomeysVisible = isSelfHosted === false && homeys.length > 0;

  const homeysLabel = i18n.messageFormatter('navigation.profile.homeys');

  function renderItemContent(
    glyphSource: string,
    label: string,
    options?: { mirrorRTL?: boolean; hasSubmenu?: boolean },
  ) {
    return (state: MenuItemRenderState) => {
      return (
        <MainNavProfileMenuItemContent
          glyphSource={glyphSource}
          label={label}
          isPressed={state.isPressed}
          isOpen={state.isOpen}
          mirrorRTL={options?.mirrorRTL}
          hasSubmenu={options?.hasSubmenu}
        />
      );
    };
  }

  function renderHomeysSubmenu(
    submenuProps: SubmenuTriggerAria<MainNavHomey>['submenuProps'],
    submenuRef: RefObject<HTMLUListElement | null>,
  ) {
    return (
      <div className={styles.homeys}>
        <Menu {...submenuProps} ref={submenuRef} items={homeys} onAction={handleHomeyAction}>
          {(homey: MainNavHomey) => {
            const isCurrent = homey.id === currentHomeyId;

            return (
              <Menu.Item
                key={homey.id}
                textValue={homey.name}
                aria-current={isCurrent ? 'true' : undefined}
              >
                <MainNavHomeyItem homey={homey} isSelected={isCurrent} />
              </Menu.Item>
            );
          }}
        </Menu>
      </div>
    );
  }

  // Sections are separated by a divider. Collections skip empty sections, not empty items, so
  // the optional items are collected before building their section.
  const sections: ReactElement<SectionProps<object>>[] = [];

  if (isPresenceVisible) {
    const presenceLabel = i18n.messageFormatter('navigation.profile.presence');

    sections.push(
      <Menu.Section key={'presenceSection'}>
        <Menu.Item
          key={MENU_KEY.presence}
          textValue={presenceLabel}
          renderContent={renderItemContent(glyph.system.presence, presenceLabel)}
        >
          {presenceLabel}
        </Menu.Item>
      </Menu.Section>,
    );
  }

  const appearanceLabel = i18n.messageFormatter('navigation.profile.appearance');

  sections.push(
    <Menu.Section key={'appearanceSection'}>
      <Menu.Item
        key={MENU_KEY.appearance}
        textValue={appearanceLabel}
        renderContent={renderItemContent(glyph.system.appearanceSettings, appearanceLabel)}
      >
        {appearanceLabel}
      </Menu.Item>
    </Menu.Section>,
  );

  if (isSelfHosted === false) {
    const accountLabel = i18n.messageFormatter('navigation.profile.account');
    const accountItems: ReactElement<ItemProps<object>>[] = [];

    if (isHomeysVisible) {
      accountItems.push(
        <Menu.Item
          key={MENU_KEY.homeys}
          textValue={homeysLabel}
          submenuOverlayProps={{ offset: suPx(1.5), crossOffset: -suPx(1) }}
          renderSubmenu={renderHomeysSubmenu}
          renderContent={renderItemContent(glyph.system.home, homeysLabel, { hasSubmenu: true })}
        >
          {homeysLabel}
        </Menu.Item>,
      );
    }

    accountItems.push(
      <Menu.Item
        key={MENU_KEY.account}
        textValue={accountLabel}
        renderContent={renderItemContent(glyph.core.personFill, accountLabel)}
      >
        {accountLabel}
      </Menu.Item>,
    );

    sections.push(<Menu.Section key={'accountSection'}>{accountItems}</Menu.Section>);
  }

  const signOutLabel = i18n.messageFormatter('navigation.profile.signOut');

  sections.push(
    <Menu.Section key={'signOutSection'}>
      <Menu.Item
        key={MENU_KEY.signOut}
        textValue={signOutLabel}
        renderContent={renderItemContent(glyph.core.doorArrowRight, signOutLabel, {
          mirrorRTL: true,
        })}
      >
        {signOutLabel}
      </Menu.Item>
    </Menu.Section>,
  );

  function handleAction(key: Key) {
    switch (key) {
      case MENU_KEY.presence:
        if (userMe != null) {
          RouteManager.toUserPresence(userMe.id);
        }
        return;
      case MENU_KEY.appearance:
        props.onAppearancePress();
        return;
      case MENU_KEY.account:
        window.open(ACCOUNT_URL, '_blank', 'noopener,noreferrer');
        return;
      case MENU_KEY.signOut:
        authStore.logout().catch(ToastManager.handleError);
        return;
      default:
        return;
    }
  }

  function handleHomeyAction(homeyId: string) {
    if (homeyId === currentHomeyId) {
      return;
    }

    RouteManager.toHomey(homeyId);
  }

  return (
    <div className={styles.root}>
      <Menu
        aria-label={i18n.messageFormatter('navigation.profile.title')}
        menuTriggerState={props.menuState}
        onAction={handleAction}
        onClose={props.menuState.close}
      >
        {sections}
      </Menu>
    </div>
  );
}
